// ============================================================================
// MemberFormDialog — wizard pre-provisionner / modifier un membre (AD-C)
// ============================================================================
// Porte des PoC new-user-dialog.tsx + member-edit-dialog.tsx sur GsWizardDialog
// (AD-0, @gs/ui). DIVERGENCES TRANCHEES vs le PoC :
//   STOP-4a : AUCUN select de role — ni a la creation (le PoC en avait un) ni en
//   edition. Le role est AFFICHE en badge, resolu Systeme C, et l'UI dit « gere
//   via la Console UG ». La creation fait une FICHE RH (compte lie au 1er login
//   Entra — P-ADM-6), pas un compte.
//   STOP-5 : AUCUN bloc « changer le courriel » (pas de bouton mort) — le rekey
//   n'est pas porte, un vrai changement d'identite part d'Entra.

import { useEffect, useState } from 'react';
import { GsWizardDialog, GsSelect, GS_COMPONENTS, type GsWizardStep } from '@gs/ui';
import { useTranslation } from '../../i18n';
import type {
  AdminMember, AdminMemberTeam, MemberCreatePayload, MemberUpdatePayload,
} from '../../hooks/useAdminMembers';
import { type GestAdminRole } from '../../hooks/useAdminAudit';

export interface MemberFormValues {
  email: string;
  fullName: string;
  matricule: string;
  hireDate: string;
  teamId: string;
  committee: string;
  managerEmail: string;
}

const EMPTY_VALUES: MemberFormValues = {
  email: '', fullName: '', matricule: '', hireDate: '', teamId: '', committee: '', managerEmail: '',
};

export function initialMemberValues(member: AdminMember): MemberFormValues {
  return {
    email: member.email,
    fullName: member.fullName ?? '',
    matricule: member.matricule ?? '',
    hireDate: '',
    teamId: member.teamId ?? '',
    committee: member.committee ?? '',
    managerEmail: member.managerEmail ?? '',
  };
}

const toNull = (v: string): string | null => (v.trim() ? v.trim() : null);
const toCommittee = (v: string): 'ce' | 'cg' | null => (v === 'ce' || v === 'cg' ? v : null);

export function MemberFormDialog({
  open,
  mode,
  member,
  memberRole,
  initial,
  teams,
  managerOptions,
  isPending,
  onClose,
  onCreate,
  onUpdate,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  /** Cible en edition — ignore en creation. */
  member?: AdminMember;
  /** Role effectif AFFICHE (badge STOP-4a) — derive par MembersTab. */
  memberRole?: GestAdminRole;
  initial?: MemberFormValues;
  teams: AdminMemberTeam[];
  managerOptions: { value: string; label: string }[];
  isPending: boolean;
  onClose: () => void;
  onCreate: (payload: MemberCreatePayload) => void;
  onUpdate: (payload: MemberUpdatePayload) => void;
}) {
  const { t } = useTranslation();
  const [values, setValues] = useState<MemberFormValues>(EMPTY_VALUES);

  // Re-seed a chaque ouverture (le wizard revient a l'etape 1 de lui-meme).
  useEffect(() => {
    if (open) setValues(mode === 'edit' && initial ? initial : EMPTY_VALUES);
  }, [open, mode, initial]);

  const set = (patch: Partial<MemberFormValues>) => setValues((v) => ({ ...v, ...patch }));

  const labelCls = 'text-meta font-medium text-slate-600 dark:text-slate-300';
  const hintCls = 'text-meta text-slate-400 dark:text-slate-500';

  const teamOptions = teams.map((tm) => ({ value: tm.id, label: tm.name }));
  const committeeOptions = [
    { value: 'ce', label: t('gest:profile.committees.ce') },
    { value: 'cg', label: t('gest:profile.committees.cg') },
  ];
  // Responsable actuel absent du pool (fiche inactivee) : on l'ajoute pour que
  // la valeur reste affichable — parite PoC « ne pas vider le select ».
  const managerSelect = managerOptions.filter((o) => o.value !== values.email);
  if (values.managerEmail && !managerSelect.some((o) => o.value === values.managerEmail)) {
    managerSelect.push({ value: values.managerEmail, label: values.managerEmail });
  }

  // ── Etape 1 (creation) : identite — email cle, matricule + embauche requis ──
  const identityStep: GsWizardStep = {
    id: 'identity',
    title: t('gest:admin.members.form.stepIdentity'),
    canProceed: () =>
      values.email.trim().includes('@') && values.matricule.trim().length > 0 && !!values.hireDate,
    content: (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="member-form-email" className={labelCls}>
            {t('gest:admin.members.form.email')}
          </label>
          <input
            id="member-form-email"
            type="email"
            autoFocus
            maxLength={320}
            placeholder={t('gest:admin.members.form.emailPlaceholder')}
            value={values.email}
            onChange={(e) => set({ email: e.target.value })}
            className={GS_COMPONENTS.input}
          />
          <p className={hintCls}>{t('gest:admin.members.form.emailHint')}</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="member-form-name" className={labelCls}>
            {t('gest:admin.members.form.fullName')}
          </label>
          <input
            id="member-form-name"
            maxLength={200}
            placeholder={t('gest:admin.members.form.fullNamePlaceholder')}
            value={values.fullName}
            onChange={(e) => set({ fullName: e.target.value })}
            className={GS_COMPONENTS.input}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="member-form-matricule" className={labelCls}>
              {t('gest:admin.members.form.matricule')}
            </label>
            <input
              id="member-form-matricule"
              maxLength={40}
              placeholder={t('gest:admin.members.form.matriculePlaceholder')}
              value={values.matricule}
              onChange={(e) => set({ matricule: e.target.value })}
              className={GS_COMPONENTS.input}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="member-form-hire" className={labelCls}>
              {t('gest:admin.members.form.hireDate')}
            </label>
            <input
              id="member-form-hire"
              type="date"
              value={values.hireDate}
              onChange={(e) => set({ hireDate: e.target.value })}
              className={GS_COMPONENTS.input}
            />
            <p className={hintCls}>{t('gest:admin.members.form.hireDateHint')}</p>
          </div>
        </div>
      </div>
    ),
  };

  // ── Etape rattachement (creation ET edition) — SANS role (STOP-4a) ──────────
  const attachmentStep: GsWizardStep = {
    id: 'attachment',
    title: t('gest:admin.members.form.stepAttachment'),
    content: (
      <div className="flex flex-col gap-4">
        {mode === 'edit' ? (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="member-form-name-edit" className={labelCls}>
                  {t('gest:admin.members.form.fullName')}
                </label>
                <input
                  id="member-form-name-edit"
                  maxLength={200}
                  value={values.fullName}
                  onChange={(e) => set({ fullName: e.target.value })}
                  className={GS_COMPONENTS.input}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="member-form-matricule-edit" className={labelCls}>
                  {t('gest:admin.members.form.matricule')}
                </label>
                <input
                  id="member-form-matricule-edit"
                  maxLength={40}
                  value={values.matricule}
                  onChange={(e) => set({ matricule: e.target.value })}
                  className={GS_COMPONENTS.input}
                />
              </div>
            </div>
            {/* STOP-4a : role AFFICHE seulement — jamais un select. */}
            <div className="flex flex-col gap-1.5">
              <span className={labelCls}>{t('gest:admin.members.columns.role')}</span>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex w-fit items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-chip font-medium uppercase text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  {t(`gest:profile.roles.${memberRole ?? 'employee'}`)}
                </span>
                <span className={hintCls}>{t('gest:admin.members.roleHint')}</span>
              </div>
            </div>
          </>
        ) : null}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className={labelCls} id="member-form-team-label">
              {t('gest:admin.members.form.team')}
            </label>
            <GsSelect
              value={values.teamId}
              onChange={(teamId) => set({ teamId })}
              options={teamOptions}
              placeholder={t('gest:admin.members.form.teamNone')}
              ariaLabel={t('gest:admin.members.form.team')}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className={labelCls} id="member-form-committee-label">
              {t('gest:admin.members.form.committee')}
            </label>
            <GsSelect
              value={values.committee}
              onChange={(committee) => set({ committee })}
              options={committeeOptions}
              placeholder={t('gest:admin.members.form.committeeNone')}
              ariaLabel={t('gest:admin.members.form.committee')}
            />
          </div>
        </div>
        {mode === 'edit' ? (
          <div className="flex flex-col gap-1.5">
            <label className={labelCls} id="member-form-manager-label">
              {t('gest:admin.members.form.manager')}
            </label>
            <GsSelect
              value={values.managerEmail}
              onChange={(managerEmail) => set({ managerEmail })}
              options={managerSelect}
              placeholder={t('gest:admin.members.form.managerNone')}
              ariaLabel={t('gest:admin.members.form.manager')}
            />
          </div>
        ) : (
          <p className={hintCls}>{t('gest:admin.members.roleHint')}</p>
        )}
      </div>
    ),
  };

  const steps = mode === 'create' ? [identityStep, attachmentStep] : [attachmentStep];

  return (
    <GsWizardDialog
      open={open}
      onClose={onClose}
      title={
        mode === 'create'
          ? t('gest:admin.members.form.createTitle')
          : t('gest:admin.members.form.editTitle', {
              name: member?.fullName ?? member?.email ?? '',
            })
      }
      steps={steps}
      isLoading={isPending}
      previousLabel={t('gest:admin.members.form.back')}
      nextLabel={t('gest:admin.members.form.next')}
      cancelLabel={t('gest:admin.members.form.cancel')}
      pendingLabel={t('gest:admin.members.form.pending')}
      completeLabel={
        mode === 'create'
          ? t('gest:admin.members.form.submitCreate')
          : t('gest:admin.members.form.submitEdit')
      }
      onComplete={() => {
        if (mode === 'create') {
          onCreate({
            email: values.email.trim().toLowerCase(),
            fullName: toNull(values.fullName),
            matricule: values.matricule.trim(),
            hireDate: values.hireDate,
            teamId: toNull(values.teamId),
            committee: toCommittee(values.committee),
          });
        } else {
          onUpdate({
            fullName: toNull(values.fullName),
            matricule: toNull(values.matricule),
            teamId: toNull(values.teamId),
            committee: toCommittee(values.committee),
            managerEmail: toNull(values.managerEmail),
          });
        }
      }}
    />
  );
}
